import { Condition } from '@medplum/fhirtypes';
import { FC } from 'react';
import { getDisplayString } from '@medplum/core';
import { ResourceListSection } from '../shared/ResourceListSection';
import { ResourceListSectionConfig } from '../shared/ResourceListSectionConfig';
import { HpoListItem } from './HpoListItem';
import { createActiveFilter } from '../shared/resource-sort-utils';

// -----------------------------------------------------------------------------
export interface HpoSummaryProps {
  readonly hpoConditions: Condition[];
}

// -----------------------------------------------------------------------------
const hpoConfig: ResourceListSectionConfig<Condition> = {
  titleKey: 'summaryPage.summary.hpoConditions.title',
  noneKey: 'summaryPage.summary.hpoConditions.none',
  noResourcesKey: 'summaryPage.summary.hpoConditions.none',
  hideDate: true,

  sortResources: (conditions) => {
    // Filter active only, deduplicate by code, then sort by recordedDate (newest first)
    const activeFilter = createActiveFilter<Condition>(
      (c) => c.clinicalStatus?.coding?.[0]?.code
    );
    const seen = new Set<string>();
    return activeFilter(conditions)
      .filter((condition) => {
        const code = condition.code?.coding?.[0];
        const key = `${code?.system ?? 'unknown'}|${code?.code ?? getDisplayString(condition)}`;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      })
      .sort((a, b) =>
        (b.recordedDate ?? '').localeCompare(a.recordedDate ?? '')
      );
  },

  getDisplayDate: (condition) =>
    condition.recordedDate || condition.onsetDateTime || '',

  getDisplayName: (condition) =>
    condition.code?.coding?.[0]?.display ||
    condition.code?.text ||
    getDisplayString(condition),

  getStatusLabel: (condition, t) =>
    t(
      `diagnosis.clinicalStatus.${condition.clinicalStatus?.coding?.[0]?.code}`,
      {
        defaultValue: condition.clinicalStatus?.coding?.[0]?.display,
      }
    ),

  renderItem: (condition) => <HpoListItem hpoCondition={condition} />,
};

// -----------------------------------------------------------------------------
export const HpoSummary: FC<HpoSummaryProps> = ({ hpoConditions }) => {
  return (
    <ResourceListSection<Condition>
      resources={hpoConditions}
      config={hpoConfig}
    />
  );
};
